import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
// core
import { LinearProgress, Typography } from '@material-ui/core';
// components
import Button from '../../components/Button';
import Snackbar from '../../components/Snackbar';
import ConfirmDialog from '../../components/ConfirmDialog';
import ValidationTable from './ValidationTable';

import useStyles from './styles';
import { translateFileError } from './issues';
import {
  getBulkAssociationTemplate,
  postValidateBulkAssociationFile,
  postLoadBulkAssociationFile,
} from '../../requests/api/bulk-association';

const TEMPLATE_FILENAME = 'bulk-association-template.xlsx';
const XLSX_ACCEPT = '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function BulkAssociation() {
  const styles = useStyles();
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const [downloading, setDownloading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validatedRows, setValidatedRows] = useState([]);
  // Se guarda el archivo validado para reenviarlo al cargar: el API lo revalida.
  const [validatedFile, setValidatedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [err, setErr] = useState('');
  const [succ, setSucc] = useState('');

  const rowsToLoad = validatedRows.filter((row) => row.load).length;

  let progressLabel = null;
  if (downloading) progressLabel = t('bulkAssociation.progress.downloading');
  if (validating) progressLabel = t('bulkAssociation.progress.validating');
  if (loading) progressLabel = t('bulkAssociation.progress.loading');

  const handleDownloadTemplate = async () => {
    setDownloading(true);

    const result = await getBulkAssociationTemplate();

    if (result.success) {
      const url = window.URL.createObjectURL(new Blob([result.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', TEMPLATE_FILENAME);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSucc(t('bulkAssociation.messages.templateDownloaded'));
    } else {
      setErr(t('bulkAssociation.messages.templateDownloadError'));
    }

    setDownloading(false);
  };

  const handleSelectFile = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    const [file] = event.target.files;
    // Permite volver a subir el mismo archivo después de corregirlo.
    fileInputRef.current.value = '';
    if (!file) return;

    setValidating(true);

    const result = await postValidateBulkAssociationFile(file);

    if (result.success) {
      setValidatedRows(result.data.rows);
      setValidatedFile(file);
      setSucc(t('bulkAssociation.messages.fileValidated'));
    } else {
      setValidatedRows([]);
      setValidatedFile(null);
      setErr(translateFileError(t, result.data, 'bulkAssociation.messages.fileValidationError'));
    }

    setValidating(false);
  };

  const handleLoadData = async () => {
    setConfirmOpen(false);
    setLoading(true);

    const result = await postLoadBulkAssociationFile(validatedFile);

    if (result.success) {
      setValidatedRows([]);
      setValidatedFile(null);
      setSucc(t('bulkAssociation.messages.dataLoaded', result.data));
    } else {
      setErr(translateFileError(t, result.data, 'bulkAssociation.messages.dataLoadError'));
    }

    setLoading(false);
  };

  const actionButtons = [
    {
      id: 'download-template-btn',
      text: t('bulkAssociation.buttons.downloadTemplate'),
      onClick: handleDownloadTemplate,
      disabled: downloading,
    },
    {
      id: 'upload-template-btn',
      text: t('bulkAssociation.buttons.uploadTemplate'),
      onClick: handleSelectFile,
      disabled: validating,
    },
    {
      id: 'load-data-btn',
      text: t('bulkAssociation.buttons.loadData'),
      onClick: () => setConfirmOpen(true),
      disabled: loading || validating || !validatedFile || rowsToLoad === 0,
    },
  ];

  return (
    <div className={styles.content}>
      <div className={styles.header}>
        <Typography variant='h4' className={styles.title}>{t('bulkAssociation.title')}</Typography>
      </div>
      <div className={styles.actions}>
        {actionButtons.map((button) => (
          <Button
            key={button.id}
            id={button.id}
            variant="outlined"
            text={button.text}
            onClick={button.onClick}
            disabled={button.disabled}
            height="40px"
            width="180px"
            backgroundColor={button.disabled ? 'theme-custom-disabledButtonBackground' : 'theme-custom-solidButtonBackground'}
            color='theme-custom-solidButtonText'
            border={button.disabled ? 'none' : undefined}
            borderRadius='10px'
          />
        ))}
        <input
          ref={fileInputRef}
          type="file"
          accept={XLSX_ACCEPT}
          className={styles.hiddenInput}
          onChange={handleFileChange}
          aria-label={t('bulkAssociation.buttons.uploadTemplate')}
        />
      </div>
      {progressLabel && (
        <div className={styles.progress} role="status">
          <Typography variant="body2">{progressLabel}</Typography>
          <LinearProgress aria-label={progressLabel} />
        </div>
      )}
      <ValidationTable rows={validatedRows} />
      <ConfirmDialog
        open={confirmOpen}
        title={t('bulkAssociation.confirm.title')}
        message={t('bulkAssociation.confirm.message', { count: rowsToLoad })}
        description={t('bulkAssociation.confirm.description')}
        confirmText={t('bulkAssociation.confirm.confirm')}
        cancelText={t('bulkAssociation.confirm.cancel')}
        onConfirm={handleLoadData}
        onCancel={() => setConfirmOpen(false)}
      />
      <Snackbar
        open={err !== ''}
        message={err}
        severity="error"
        onClose={() => setErr('')}
      />
      <Snackbar
        open={succ !== ''}
        message={succ}
        severity="success"
        onClose={() => setSucc('')}
      />
    </div>
  );
}

export default BulkAssociation;
